import { useParams } from "@tanstack/react-router";
import { Card, Descriptions, Space, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecentlyViewed } from "@/shared/hooks/useRecentlyViewed";
import { LeadAnswers } from "../components/LeadAnswers";
import { useLeadDetail } from "../hooks/useLeads";
import { SUSPICION_LABELS } from "../types";

const { Title } = Typography;

/**
 * ⚠️ `useParams({ strict: false })` — marshrut obyekti `routeTree.tsx`
 * da (poydevor fayli) va uni import qilish F2 dan poydevorga qayta
 * bog'lanish yaratardi. `strict: false` tiplashni yumshatadi, lekin
 * `leadId` ning mavjudligi marshrut ta'rifi bilan kafolatlangan.
 */
export function LeadDetailPage() {
	const { t } = useTranslation("leads");
	const params = useParams({ strict: false }) as { leadId?: string };
	const { data: lead, isLoading } = useLeadDetail(params.leadId);
	const { remember } = useRecentlyViewed();

	// ⚠️ `lead` kelgandan KEYIN eslanadi — aks holda ro'yxatda yuklanish
	// paytidagi bo'sh yorliq qolardi.
	useEffect(() => {
		if (lead) {
			remember({ path: `/leads/${lead.id}`, label: `Lid · ${lead.formName}` });
		}
	}, [lead, remember]);

	if (isLoading || !lead) {
		return <Spin />;
	}

	return (
		<Space direction="vertical" size="large" className="w-full">
			<Title level={3}>{t("detail.title")}</Title>

			<Card title={t("detail.generalCard")}>
				<Descriptions bordered column={2} size="small">
					<Descriptions.Item label={t("detail.employee")}>
						{lead.employeeName ?? lead.employeeId}
					</Descriptions.Item>
					<Descriptions.Item label={t("detail.form")}>
						{t("detail.formValue", { name: lead.formName, version: lead.formVersion })}
					</Descriptions.Item>
					{/* ⚠️ Ikkala vaqt ham ko'rsatiladi — oflayn lid uchun ular
					    sezilarli farq qiladi (spec §8.2, 5-band). */}
					<Descriptions.Item label={t("detail.occurredAt")}>
						{dayjs(lead.occurredAt).format("YYYY-MM-DD HH:mm")}
					</Descriptions.Item>
					<Descriptions.Item label={t("detail.recordedAt")}>
						{dayjs(lead.recordedAt).format("YYYY-MM-DD HH:mm")}
					</Descriptions.Item>
					<Descriptions.Item label={t("detail.location")}>{lead.resolvedLabel}</Descriptions.Item>
					<Descriptions.Item label={t("detail.coordinates")}>
						{lead.lat !== null && lead.lon !== null
							? `${lead.lat.toFixed(6)}, ${lead.lon.toFixed(6)}`
							: "—"}
					</Descriptions.Item>
					<Descriptions.Item label={t("detail.task")}>{lead.taskName ?? "—"}</Descriptions.Item>
					<Descriptions.Item label={t("detail.flags")}>
						{lead.suspicionFlags.length === 0
							? "—"
							: lead.suspicionFlags.map((flag) => (
									<Tag color="orange" key={flag}>
										{t(`suspicion.${flag}`, SUSPICION_LABELS[flag] ?? flag)}
									</Tag>
								))}
					</Descriptions.Item>
				</Descriptions>
			</Card>

			<Card title={t("detail.answersCard")}>
				<LeadAnswers schema={lead.formSchema} answers={lead.answers} />
			</Card>
		</Space>
	);
}
